<template>
    <div class="manual-marker-container">
        <!-- Version compacte pour la ligne d'outils -->
        <div class="poi-compact">
            <button
                class="btn btn-sm"
                :class="isAddingMarker ? 'btn-success' : 'btn-outline-primary'"
                @click="toggleAddingMode"
                :title="buttonTitle"
            >
                <i class="fas" :class="isAddingMarker ? 'fa-plus' : 'fa-star'"></i>
                {{ buttonText }}
                <span class="poi-count badge bg-secondary ms-1">{{ manualMarkers.length }}</span>
            </button>

            <!-- Bouton ajouter avec coordonnées manuelles -->
            <button
                v-if="!isAddingMarker"
                class="btn btn-sm btn-outline-warning ms-1"
                @click="addMarkerWithCoordinates"
                title="Ajouter avec coordonnées manuelles"
            >
                <i class="fas fa-map-marker-alt"></i>
                Coordonnées
            </button>

            <!-- Bouton revenir à la vue habituelle -->
            <button
                v-if="!isAddingMarker"
                class="btn btn-sm btn-outline-info ms-1"
                @click="resetMapView"
                title="Revenir à la vue habituelle"
            >
                <i class="fas fa-undo"></i>
                Retablir vue
            </button>

            <!-- Dropdown liste des markers -->
            <div v-if="manualMarkers.length > 0 && !isAddingMarker" class="poi-dropdown ms-1">
                <div class="dropdown">
                    <button
                        class="btn btn-sm btn-outline-secondary dropdown-toggle"
                        type="button"
                        @click="toggleDropdown"
                    >
                        <i class="fas fa-list"></i>
                        Marqueurs
                    </button>

                    <div v-if="showDropdown" class="dropdown-menu show poi-dropdown-menu">
                        <!-- Barre de recherche -->
                        <div class="poi-search">
                            <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model="searchQuery"
                                placeholder="Rechercher un marqueur..."
                            >
                        </div>

                        <div class="poi-dropdown-list">
                            <div
                                v-for="marker in filteredMarkers"
                                :key="marker.id"
                                class="poi-dropdown-item"
                            >
                                <div class="d-flex align-items-center">
                                    <span class="poi-icon" :style="{ color: marker.color || '#FFA500' }">
                                        <i class="fas fa-star"></i>
                                    </span>
                                    <span class="poi-name">{{ marker.name || 'Sans nom' }}</span>
                                </div>
                                <div class="btn-group">
                                    <button
                                        class="btn btn-sm btn-outline-warning"
                                        @click="editMarker(marker)"
                                        title="Modifier"
                                    >
                                        <i class="fas fa-pencil-alt"></i>
                                    </button>
                                    <button
                                        class="btn btn-sm btn-outline-primary"
                                        @click="focusOnMarker(marker)"
                                        title="Voir"
                                    >
                                        <i class="fas fa-eye"></i>
                                    </button>
                                    <button
                                        class="btn btn-sm btn-outline-danger"
                                        @click="confirmDeleteMarker(marker)"
                                        title="Supprimer"
                                    >
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Message si aucun résultat -->
                            <div v-if="filteredMarkers.length === 0" class="no-results">
                                <small class="text-muted">
                                    <i class="fas fa-search"></i>
                                    Aucun marqueur trouvé pour "{{ searchQuery }}"
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal de création/édition de marker -->
        <div v-if="showMarkerModal" class="modal-overlay" @click="closeModal">
            <div class="modal-content" @click.stop>
                <div class="modal-header">
                    <h5>{{ editingMarker ? 'Modifier Marker' : 'Nouveau Marker' }}</h5>
                    <button type="button" class="btn-close" @click="closeModal"></button>
                </div>
                <div class="modal-body">
                    <!-- Sélecteur de format de coordonnées -->
                    <div class="mb-3">
                        <label class="form-label">Format des coordonnées</label>
                        <select class="form-select" v-model="coordinateFormat" @change="onCoordinateFormatChange">
                            <option value="decimal">Décimal (ex: 9.5704107, 3.0290977)</option>
                            <option value="utm">UTM (ex: 31N 543210 1058765)</option>
                            <option value="mgrs">MGRS (ex: 31PNA5432110587)</option>
                        </select>
                    </div>

                    <!-- Coordonnées selon le format -->
                    <div class="mb-3">
                        <label class="form-label">{{ coordinateLabel }}</label>
                        <small class="text-muted d-block mb-2" v-if="!editingMarker && tempMarker">
                            Coordonnées du point sélectionné sur la carte (modifiables)
                        </small>
                        <small class="text-muted d-block mb-2" v-else-if="!editingMarker && !tempMarker">
                            Entrez les coordonnées manuellement ou choisissez un format
                        </small>

                        <!-- Format Décimal -->
                        <div v-if="coordinateFormat === 'decimal'" class="row">
                            <div class="col-6">
                                <input
                                    type="number"
                                    step="any"
                                    class="form-control"
                                    v-model.number="markerForm.latitude"
                                    placeholder="Latitude"
                                >
                            </div>
                            <div class="col-6">
                                <input
                                    type="number"
                                    step="any"
                                    class="form-control"
                                    v-model.number="markerForm.longitude"
                                    placeholder="Longitude"
                                >
                            </div>
                        </div>

                        <!-- Format UTM -->
                        <div v-else-if="coordinateFormat === 'utm'" class="row">
                            <div class="col-4">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="utmForm.zone"
                                    placeholder="30N"
                                >
                            </div>
                            <div class="col-4">
                                <input
                                    type="number"
                                    step="any"
                                    class="form-control"
                                    v-model.number="utmForm.easting"
                                    placeholder="654321"
                                >
                            </div>
                            <div class="col-4">
                                <input
                                    type="number"
                                    step="any"
                                    class="form-control"
                                    v-model.number="utmForm.northing"
                                    placeholder="1234567"
                                >
                            </div>
                        </div>

                        <!-- Format MGRS -->
                        <div v-else-if="coordinateFormat === 'mgrs'">
                            <input
                                type="text"
                                class="form-control"
                                v-model="mgrsForm.coordinate"
                                placeholder="31NDF3200004000"
                                @blur="cleanMgrsInput"
                            >
                            <small class="text-muted">Format compact sans espaces (ex: 31NDF3200004000)</small>
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Nom (optionnel)</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="markerForm.name"
                            placeholder="Ex: Point de rendez-vous"
                        >
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Description (optionnel)</label>
                        <textarea
                            class="form-control"
                            v-model="markerForm.description"
                            rows="3"
                            placeholder="Description du marker..."
                        ></textarea>
                    </div>

                    <div class="mb-3">
                        <label class="form-label">Couleur</label>
                        <input
                            type="color"
                            class="form-control form-control-color"
                            v-model="markerForm.color"
                            style="height: 38px;"
                        >
                    </div>

                    <small class="text-muted" v-if="coordinateFormat !== 'decimal'">
                        ℹ️ Les coordonnées seront automatiquement converties en décimal lors de l'enregistrement
                    </small>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeModal">Annuler</button>
                    <button
                        type="button"
                        class="btn btn-primary"
                        @click="saveMarker"
                        :disabled="isSaving"
                    >
                        <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
                        {{ isSaving ? 'Enregistrement...' : (editingMarker ? 'Modifier' : 'Enregistrer') }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import leaflet from 'leaflet';

export default {
    name: 'ManualMarker',
    props: {
        map: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            isAddingMarker: false,
            showMarkerModal: false,
            isSaving: false,
            tempMarker: null,
            markerForm: {
                name: '',
                description: '',
                color: '#FFA500', // Orange par défaut (pas jaune)
                latitude: null,
                longitude: null
            },
            coordinateFormat: 'decimal', // decimal, utm, mgrs
            utmForm: {
                zone: '',
                easting: null,
                northing: null
            },
            mgrsForm: {
                coordinate: ''
            },
            searchQuery: '', // Pour la recherche dans les marqueurs
            clickHandler: null,
            leafletMarkers: [],
            showDropdown: false,
            editingMarker: null
        };
    },
    computed: {
        ...mapState('markers/list', {
            manualMarkers: 'items',
            isLoading: 'isLoading'
        }),
        buttonTitle() {
            return this.isAddingMarker ? "Arrêter l'ajout de marqueurs" : 'Ajouter un marqueur manuel';
        },
        buttonText() {
            return this.isAddingMarker ? "Arrêter" : 'Ajt marqueur';
        },
        coordinateLabel() {
            switch(this.coordinateFormat) {
                case 'decimal': return 'Coordonnées (Latitude, Longitude)';
                case 'utm': return 'UTM (Zone, Easting, Northing)';
                case 'mgrs': return 'MGRS (Coordonnée militaire)';
                default: return 'Coordonnées';
            }
        },
        filteredMarkers() {
            if (!this.searchQuery || this.searchQuery.trim() === '') {
                return this.manualMarkers;
            }

            const query = this.searchQuery.toLowerCase().trim();
            return this.manualMarkers.filter(marker => {
                const name = (marker.name || 'Sans nom').toLowerCase();
                const description = (marker.description || '').toLowerCase();
                return name.includes(query) || description.includes(query);
            });
        }
    },
    methods: {
        ...mapActions('markers/list', [
            'getMarkers',
            'createMarker',
            'updateMarker',
            'deleteMarker'
        ]),

        toggleDropdown() {
            this.showDropdown = !this.showDropdown;
        },

        resetMapView() {
            this.map.setView([9.5704107, 3.0290977], 7);

            this.$emit('show-toast', '🌍 Vue par défaut rétablie');
        },

        toggleAddingMode() {
            this.isAddingMarker = !this.isAddingMarker;

            if (this.isAddingMarker) {
                // Activer le mode d'ajout : ajouter un écouteur de clic sur la carte
                this.clickHandler = (e) => {
                    this.onMapClick(e);
                };
                this.map.on('click', this.clickHandler);
                this.map.getContainer().style.cursor = 'crosshair';
                this.$emit('show-toast', '📍 Cliquez sur la carte pour placer un marker');
            } else {
                // Désactiver le mode d'ajout
                if (this.clickHandler) {
                    this.map.off('click', this.clickHandler);
                    this.clickHandler = null;
                }
                this.map.getContainer().style.cursor = '';
            }
        },

        addMarkerWithCoordinates() {
            // Ouvrir le modal sans avoir cliqué sur la carte
            this.tempMarker = null;
            this.markerForm.latitude = null;
            this.markerForm.longitude = null;
            this.coordinateFormat = 'decimal';
            this.showMarkerModal = true;
            this.$emit('show-toast', '📍 Entrez les coordonnées manuellement');
        },

        onMapClick(e) {
            this.tempMarker = {
                lat: e.latlng.lat,
                lng: e.latlng.lng
            };
            // Initialiser les coordonnées dans markerForm pour le format décimal par défaut
            this.markerForm.latitude = e.latlng.lat;
            this.markerForm.longitude = e.latlng.lng;
            this.coordinateFormat = 'decimal'; // Format par défaut
            this.showMarkerModal = true;
        },

        closeModal() {
            this.showMarkerModal = false;
            this.tempMarker = null;
            this.editingMarker = null;
            this.coordinateFormat = 'decimal';
            this.markerForm = {
                name: '',
                description: '',
                color: '#FFA500',
                latitude: null,
                longitude: null
            };
            this.utmForm = {
                zone: '',
                easting: null,
                northing: null
            };
            this.mgrsForm = {
                coordinate: ''
            };
        },

        editMarker(marker) {
            this.editingMarker = marker;
            this.markerForm = {
                name: marker.name || '',
                description: marker.description || '',
                color: marker.color || '#FFA500',
                latitude: parseFloat(marker.latitude),
                longitude: parseFloat(marker.longitude)
            };
            this.coordinateFormat = 'decimal'; // Toujours commencer avec le format stocké
            this.showMarkerModal = true;
            this.showDropdown = false;
        },

        onCoordinateFormatChange() {
            // Réinitialiser les formulaires quand on change de format
            if (this.coordinateFormat === 'decimal') {
                // Convertir depuis les coordonnées actuelles vers décimal
                // Les coordonnées sont déjà en décimal dans markerForm
            } else if (this.coordinateFormat === 'utm') {
                // Convertir décimal → UTM pour affichage
                if (this.markerForm.latitude && this.markerForm.longitude) {
                    const utm = this.decimalToUtm(this.markerForm.latitude, this.markerForm.longitude);
                    this.utmForm = utm;
                }
            } else if (this.coordinateFormat === 'mgrs') {
                // Convertir décimal → MGRS pour affichage
                if (this.markerForm.latitude && this.markerForm.longitude) {
                    this.mgrsForm.coordinate = this.decimalToMgrs(this.markerForm.latitude, this.markerForm.longitude);
                }
            }
        },

        cleanMgrsInput() {
            // Enlever automatiquement les espaces quand l'utilisateur quitte le champ
            if (this.mgrsForm.coordinate) {
                this.mgrsForm.coordinate = this.mgrsForm.coordinate.replace(/\s+/g, '').toUpperCase();
            }
        },

        // ==================== CONVERSIONS UTM/MGRS ====================

        decimalToUtm(lat, lon) {
            // Utilisation de la librairie UTM
            const utm = require('utm');
            const result = utm.fromLatLon(lat, lon);
            return {
                zone: `${result.zoneNum}${result.zoneLetter}`,
                easting: result.easting,
                northing: result.northing
            };
        },

        utmToDecimal(zone, easting, northing) {
            const utm = require('utm');
            const zoneNum = parseInt(zone.match(/\d+/)[0]);
            const zoneLetter = zone.match(/[A-Z]/)[0];

            const result = utm.toLatLon(easting, northing, zoneNum, zoneLetter);
            return {
                latitude: result.latitude,
                longitude: result.longitude
            };
        },

        decimalToMgrs(lat, lon) {
            // Utilisation de la vraie librairie MGRS
            const mgrs = require('mgrs');
            const result = mgrs.forward([lon, lat]); // MGRS utilise [lon, lat]
            return result; // Retourne déjà le format compact
        },

        mgrsToDecimal(mgrsString) {
            // Utilisation de la vraie librairie MGRS
            const mgrs = require('mgrs');
            try {
                // Nettoyer la chaîne - enlever TOUS les espaces pour le format compact
                const cleaned = mgrsString.trim().replace(/\s+/g, '').toUpperCase();

                const result = mgrs.toPoint(cleaned); // Retourne [lon, lat]

                return {
                    latitude: result[1], // MGRS retourne [lon, lat]
                    longitude: result[0]
                };
            } catch (error) {
                console.error('Erreur MGRS:', error);
                throw new Error('Format MGRS invalide');
            }
        },

        async saveMarker() {
            this.isSaving = true;

            try {
                // Conversion automatique des coordonnées si nécessaire
                let latitude, longitude;

                if (this.coordinateFormat === 'decimal') {
                    latitude = this.markerForm.latitude;
                    longitude = this.markerForm.longitude;
                } else if (this.coordinateFormat === 'utm') {
                    // UTM → Décimal
                    const decimal = this.utmToDecimal(
                        this.utmForm.zone,
                        this.utmForm.easting,
                        this.utmForm.northing
                    );
                    latitude = decimal.latitude;
                    longitude = decimal.longitude;
                    this.$emit('show-toast', '🔄 UTM converti en décimal');
                } else if (this.coordinateFormat === 'mgrs') {
                    // MGRS → Décimal
                    try {
                        const decimal = this.mgrsToDecimal(this.mgrsForm.coordinate);
                        latitude = decimal.latitude;
                        longitude = decimal.longitude;
                        this.$emit('show-toast', '🔄 MGRS converti en décimal');
                    } catch (error) {
                        this.$emit('show-toast', '❌ Format MGRS invalide. Utilisez le format compact: 31NDF3200004000');
                        this.isSaving = false;
                        return;
                    }
                }

                // Validation des coordonnées
                if (!latitude || !longitude ||
                    isNaN(latitude) || isNaN(longitude) ||
                    latitude < -90 || latitude > 90 ||
                    longitude < -180 || longitude > 180) {
                    this.$emit('show-toast', '❌ Coordonnées invalides. Vérifiez les valeurs.');
                    this.isSaving = false;
                    return;
                }

                if (this.editingMarker) {
                    // Mode édition
                    const updateData = {
                        latitude: latitude,
                        longitude: longitude,
                        name: this.markerForm.name,
                        description: this.markerForm.description,
                        color: this.markerForm.color
                    };

                    const response = await this.updateMarker({
                        id: this.editingMarker.id,
                        markerData: updateData
                    });

                    // Mettre à jour le marker sur la carte
                    this.updateMarkerOnMap(response.data);
                    this.$emit('show-toast', '✅ Marker modifié avec succès !');
                } else {
                    // Mode création
                    const markerData = {
                        latitude: latitude,
                        longitude: longitude,
                        name: this.markerForm.name,
                        description: this.markerForm.description,
                        color: this.markerForm.color
                    };

                    const response = await this.createMarker(markerData);

                    // Ajouter le marker à la carte
                    this.addMarkerToMap(response.data);

                    this.$emit('show-toast', '✅ Marker créé avec succès !');

                    // Désactiver le mode d'ajout après création
                    this.toggleAddingMode();
                }

                this.closeModal();
            } catch (error) {
                console.error('Erreur lors de la sauvegarde du marker:', error);
                this.$emit('show-toast', '❌ Erreur lors de la sauvegarde du marker');
            } finally {
                this.isSaving = false;
            }
        },

        addMarkerToMap(marker) {
            // Utiliser Font Awesome pour une meilleure coloration
            const icon = leaflet.divIcon({
                className: 'manual-marker-icon',
                html: `<div style="
                    font-size: 24px;
                    color: ${marker.color || '#FFA500'};
                    text-shadow: 0 0 8px ${marker.color || '#FFA500'}, 0 0 12px rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                "><i class="fas fa-star"></i></div>`,
                iconSize: [24, 24],
                iconAnchor: [12, 12],
                popupAnchor: [0, -12]
            });

            const leafletMarker = leaflet.marker(
                [parseFloat(marker.latitude), parseFloat(marker.longitude)],
                {
                    icon,
                    draggable: true,
                    bounceOnAdd: false,
                    autoPanOnFocus: false,
                    keyboard: false,
                    riseOnHover: false
                }
            );

            // Popup avec infos du marker
            const popupContent = `
                <div>
                    <strong>${marker.name || 'Sans nom'}</strong><br>
                    ${marker.description || ''}
                    <br>
                    <small>${parseFloat(marker.latitude).toFixed(6)}, ${parseFloat(marker.longitude).toFixed(6)}</small>
                </div>
            `;
            leafletMarker.bindPopup(popupContent);

            // Drag & drop : mise à jour des coordonnées
            leafletMarker.on('dragend', async (e) => {
                const newPosition = e.target.getLatLng();
                try {
                    await this.updateMarker({
                        id: marker.id,
                        markerData: {
                            latitude: newPosition.lat,
                            longitude: newPosition.lng
                        }
                    });
                    this.$emit('show-toast', '✅ Marker déplacé !');
                } catch (error) {
                    console.error('Erreur lors du déplacement:', error);
                    this.$emit('show-toast', '❌ Erreur lors du déplacement');
                }
            });

            // Permettre le double-clic de passer à travers pour zoomer la carte
            leafletMarker.on('dblclick', (e) => {
                e.originalEvent.stopPropagation();
                if (this.map) {
                    this.map.doubleClickZoom.enabled();
                    this.map.zoomIn();
                }
            });

            leafletMarker.addTo(this.map);
            this.leafletMarkers.push(leafletMarker);
        },

        updateMarkerOnMap(updatedMarker) {
            // Trouver le marker Leaflet correspondant
            const index = this.leafletMarkers.findIndex(lm => {
                const pos = lm.getLatLng();
                return pos.lat === parseFloat(updatedMarker.latitude) &&
                       pos.lng === parseFloat(updatedMarker.longitude);
            });

            if (index !== -1) {
                // Retirer l'ancien marker
                this.leafletMarkers[index].remove();
                this.leafletMarkers.splice(index, 1);
            }

            // Ajouter le marker mis à jour
            this.addMarkerToMap(updatedMarker);
        },

        focusOnMarker(marker) {
            this.map.setView([parseFloat(marker.latitude), parseFloat(marker.longitude)], 12);

            // Trouver le marker Leaflet correspondant et ouvrir le popup
            const leafletMarker = this.leafletMarkers.find(lm => {
                const pos = lm.getLatLng();
                return pos.lat === parseFloat(marker.latitude) && pos.lng === parseFloat(marker.longitude);
            });

            if (leafletMarker) {
                leafletMarker.openPopup();
            }
        },

        async confirmDeleteMarker(marker) {
            if (confirm(`Supprimer le marker "${marker.name || 'Sans nom'}" ?`)) {
                try {
                    await this.deleteMarker(marker.id);

                    // Retirer le marker de la carte
                    const index = this.leafletMarkers.findIndex(lm => {
                        const pos = lm.getLatLng();
                        return pos.lat === parseFloat(marker.latitude) && pos.lng === parseFloat(marker.longitude);
                    });

                    if (index !== -1) {
                        this.leafletMarkers[index].remove();
                        this.leafletMarkers.splice(index, 1);
                    }

                    this.$emit('show-toast', '✅ Marker supprimé !');
                } catch (error) {
                    console.error('Erreur lors de la suppression:', error);
                    this.$emit('show-toast', '❌ Erreur lors de la suppression');
                }
            }
        },

        async loadMarkers() {
            // Vérifier que la carte est initialisée
            if (!this.map || !this.map._container) {
                console.log('Carte pas encore initialisée, on attend...');
                // Réessayer dans 100ms
                setTimeout(() => this.loadMarkers(), 100);
                return;
            }

            try {
                await this.getMarkers();

                // Ajouter tous les markers à la carte
                this.manualMarkers.forEach(marker => {
                    this.addMarkerToMap(marker);
                });
            } catch (error) {
                console.error('Erreur lors du chargement des markers:', error);
            }
        }
    },
    mounted() {
        // Charger les markers
        this.loadMarkers();
    },
    beforeUnmount() {
        // Nettoyer les écouteurs et markers
        if (this.clickHandler) {
            this.map.off('click', this.clickHandler);
        }

        // Supprimer tous les marqueurs de la carte
        this.leafletMarkers.forEach(marker => {
            if (marker && this.map) {
                marker.remove();
            }
        });
        this.leafletMarkers = [];

        if (this.map) {
            this.map.getContainer().style.cursor = '';
        }
    }
};
</script>

<style scoped>
.manual-marker-container {
    display: inline-block;
}

.poi-compact {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.poi-dropdown {
    position: relative;
    display: inline-block;
}

.poi-count {
    font-weight: bold;
    margin-left: 5px;
}

.poi-count.badge {
    font-size: 10px;
    padding: 2px 6px;
}

.poi-dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1000;
    min-width: 280px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    margin-top: 5px;
}

.poi-search {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
}

.poi-search input {
    font-size: 12px;
}

.poi-dropdown-list {
    max-height: 300px;
    overflow-y: auto;
}

.poi-dropdown-item {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: background 0.2s;
}

.poi-dropdown-item:hover {
    background-color: #f8f9fa;
}

.poi-dropdown-item:last-child {
    border-bottom: none;
}

.poi-icon {
    font-size: 16px;
    margin-right: 8px;
    min-width: 20px;
    text-align: center;
}

.poi-name {
    font-size: 11px;
    font-weight: 500;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.no-results {
    padding: 20px;
    text-align: center;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.modal-content {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    border-bottom: 1px solid #dee2e6;
}

.modal-body {
    padding: 20px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 15px 20px;
    border-top: 1px solid #dee2e6;
}

.marker-color-indicator {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    margin-right: 10px;
    border: 1px solid #dee2e6;
}

.markers-list {
    max-height: 300px;
    overflow-y: auto;
}

.list-group-item {
    padding: 10px 15px;
}

/* Styles pour les markers manuels sur la carte */
.manual-marker-icon {
    background: transparent !important;
    border: none !important;
}

.manual-marker-icon div {
    transition: transform 0.2s ease;
    cursor: pointer;
}

.manual-marker-icon div:hover {
    transform: scale(1.3);
}

/* Dropdown styles */
.dropdown-menu.show {
    display: block;
}

.btn-group {
    gap: 3px;
}

.btn-group .btn {
    padding: 2px 8px;
    font-size: 12px;
}

.btn-group .btn-outline-warning {
    color: #ffc107;
    border-color: #ffc107;
}

.btn-group .btn-outline-warning:hover {
    background-color: #ffc107;
    color: white;
}
</style>
